/**
 * BankAccountsSection — "الحسابات المالية للشركة" (multiple bank accounts).
 *
 * - Collapsible row per account: "حساب {bank_name}" → expands to editable details.
 * - "إضافة حساب": upload the bank IBAN letter → AI reads ALL details automatically
 *   (POST /api/admin/company-docs/extract with doc_type=bank_letter) → review → save.
 * - The PRIMARY account (⭐) is the one printed on tax invoices.
 */
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import {
  Landmark, Plus, Loader2, ChevronDown, Trash2, Save, Star, Upload, Sparkles, X, Eye,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import { toast } from 'sonner';
import { getAuthHeaders, getFileUrl } from '../../../utils/authHeaders';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const BASE = `${BACKEND_URL}/api/admin/company-settings/bank-accounts`;

const FIELDS = [
  { key: 'bank_name', ar: 'اسم البنك', en: 'Bank Name' },
  { key: 'beneficiary_name', ar: 'اسم المستفيد', en: 'Beneficiary Name' },
  { key: 'account_number', ar: 'رقم الحساب', en: 'Account Number', mono: true },
  { key: 'iban', ar: 'IBAN', en: 'IBAN', mono: true },
  { key: 'swift_code', ar: 'Swift Code', en: 'Swift Code', mono: true },
  { key: 'branch', ar: 'الفرع', en: 'Branch' },
  { key: 'currency', ar: 'العملة', en: 'Currency' },
  { key: 'account_type', ar: 'نوع الحساب', en: 'Account Type' },
];

export default function BankAccountsSection({ language }) {
  const isAr = language === 'ar';
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(BASE, { headers: getAuthHeaders() });
      setAccounts(res.data?.data || []);
    } catch (err) {
      toast.error(isAr ? 'فشل تحميل الحسابات' : 'Failed to load accounts');
    } finally {
      setLoading(false);
    }
  }, [isAr]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="bank-accounts-section">
      <div className="bg-white/[0.02] backdrop-blur-xl rounded-3xl border border-white/10 p-6">
        <div className="flex items-center justify-between mb-2 flex-wrap gap-3">
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Landmark className="h-5 w-5 text-[#d4af37]" />
            {isAr ? 'الحسابات المالية للشركة' : 'Company Bank Accounts'}
            <span className="text-sm font-normal text-[#71717A]">({accounts.length})</span>
          </h2>
          <Button
            onClick={() => setShowAdd(true)}
            className="bg-[#d4af37] hover:bg-[#c9a227] text-black"
            data-testid="add-bank-account-btn"
          >
            <Plus className="h-4 w-4 me-1" />
            {isAr ? 'إضافة حساب' : 'Add account'}
          </Button>
        </div>
        <p className="text-sm text-[#A1A1AA] mb-6">
          {isAr
            ? 'أضف حسابات الشركة البنكية — ارفع رسالة الايبان ويقرأ الذكاء الاصطناعي كل التفاصيل تلقائياً. الحساب الأساسي (⭐) هو الذي يظهر على الفواتير الضريبية.'
            : 'Add company bank accounts — upload the IBAN letter and AI reads all details automatically. The primary (⭐) account appears on tax invoices.'}
        </p>

        {loading ? (
          <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin text-[#d4af37] mx-auto" /></div>
        ) : accounts.length === 0 ? (
          <div className="text-center py-14 text-[#808080]" data-testid="bank-accounts-empty">
            <Landmark className="h-12 w-12 mx-auto mb-3 opacity-30" />
            <p className="mb-4">{isAr ? 'لا توجد حسابات بنكية بعد' : 'No bank accounts yet'}</p>
            <Button onClick={() => setShowAdd(true)} className="bg-[#d4af37] hover:bg-[#c9a227] text-black">
              <Upload className="h-4 w-4 me-1" />
              {isAr ? 'أضف أول حساب برسالة الايبان' : 'Add first account via IBAN letter'}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {accounts.map((acc) => (
              <AccountRow key={acc.id} acc={acc} isAr={isAr} onChanged={load} />
            ))}
          </div>
        )}
      </div>

      <AnimatePresence>
        {showAdd && (
          <AddAccountModal
            isAr={isAr}
            onClose={() => setShowAdd(false)}
            onSaved={() => { setShowAdd(false); load(); }}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
function AccountRow({ acc, isAr, onChanged }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(acc);
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(acc); }, [acc]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${BASE}/${acc.id}`, draft, { headers: getAuthHeaders() });
      toast.success(isAr ? 'تم حفظ الحساب' : 'Account saved');
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  const setPrimary = async () => {
    try {
      await axios.put(`${BASE}/${acc.id}/primary`, {}, { headers: getAuthHeaders() });
      toast.success(isAr ? 'أصبح الحساب الأساسي للفواتير' : 'Set as invoice primary');
      onChanged();
    } catch (err) { toast.error('Failed'); }
  };

  const del = async () => {
    if (!window.confirm(isAr ? 'حذف هذا الحساب البنكي؟' : 'Delete this bank account?')) return;
    try {
      await axios.delete(`${BASE}/${acc.id}`, { headers: getAuthHeaders() });
      toast.success(isAr ? 'تم الحذف' : 'Deleted');
      onChanged();
    } catch (err) { toast.error('Failed'); }
  };

  return (
    <div
      className={`rounded-xl border bg-[#0a0a0a] overflow-hidden transition-colors ${open ? 'border-[#d4af37]/40' : 'border-white/10 hover:border-white/20'}`}
      data-testid={`bank-account-row-${acc.id}`}
    >
      {/* Collapsible header */}
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between gap-3 p-4 text-start"
        data-testid={`bank-account-toggle-${acc.id}`}
      >
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center shrink-0">
            <Landmark className="h-5 w-5 text-violet-400" />
          </div>
          <div className="min-w-0">
            <p className="text-white font-bold truncate">
              {isAr ? `حساب ${acc.bank_name || 'بنك'}` : `${acc.bank_name || 'Bank'} account`}
            </p>
            <p className="text-xs text-[#71717A] font-mono truncate" dir="ltr">{acc.iban || acc.account_number || '—'}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {acc.is_primary && (
            <span className="inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full bg-[#d4af37]/15 border border-[#d4af37]/40 text-[#d4af37] font-bold" data-testid={`bank-primary-badge-${acc.id}`}>
              <Star className="h-3 w-3 fill-current" />
              {isAr ? 'أساسي للفواتير' : 'Invoice primary'}
            </span>
          )}
          <ChevronDown className={`h-5 w-5 text-[#71717A] transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>

      {/* Expanded details */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="px-4 pb-4 pt-1 border-t border-white/5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                {FIELDS.map((f) => (
                  <div key={f.key}>
                    <Label className="text-[#A1A1AA] text-xs">{isAr ? f.ar : f.en}</Label>
                    <Input
                      value={draft[f.key] || ''}
                      onChange={(e) => setDraft((d) => ({ ...d, [f.key]: e.target.value }))}
                      className={`bg-[#111] border-[#2a2a4e] text-white mt-1 ${f.mono ? 'font-mono' : ''}`}
                      dir={f.mono ? 'ltr' : undefined}
                      data-testid={`bank-field-${f.key}-${acc.id}`}
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 mt-4 flex-wrap">
                <Button onClick={save} disabled={saving} size="sm" className="bg-[#d4af37] hover:bg-[#c9a227] text-black" data-testid={`bank-save-${acc.id}`}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin me-1" /> : <Save className="h-4 w-4 me-1" />}
                  {isAr ? 'حفظ' : 'Save'}
                </Button>
                {!acc.is_primary && (
                  <Button onClick={setPrimary} size="sm" variant="outline" className="border-[#d4af37]/40 text-[#d4af37] hover:bg-[#d4af37]/10" data-testid={`bank-set-primary-${acc.id}`}>
                    <Star className="h-4 w-4 me-1" />
                    {isAr ? 'تعيين كأساسي للفواتير' : 'Set as invoice primary'}
                  </Button>
                )}
                {acc.file_id && (
                  <a href={getFileUrl(acc.file_id)} target="_blank" rel="noreferrer">
                    <Button size="sm" variant="outline" className="border-white/10 text-white hover:bg-white/5" data-testid={`bank-view-letter-${acc.id}`}>
                      <Eye className="h-4 w-4 me-1" />
                      {isAr ? 'عرض رسالة الايبان' : 'View IBAN letter'}
                    </Button>
                  </a>
                )}
                <Button onClick={del} size="sm" variant="outline" className="border-red-500/30 text-red-400 hover:bg-red-500/10 ms-auto" data-testid={`bank-delete-${acc.id}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Add account: upload IBAN letter → AI extraction → review → save
// ---------------------------------------------------------------------------
function AddAccountModal({ isAr, onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [extracted, setExtracted] = useState(null);
  const [fileId, setFileId] = useState(null);

  const handleFile = (f) => {
    if (!f) return;
    const ok = f.type === 'application/pdf' || f.type.startsWith('image/');
    if (!ok) { toast.error(isAr ? 'النوع غير مدعوم — PDF أو صورة فقط' : 'Only PDF/image allowed'); return; }
    if (f.size > 15 * 1024 * 1024) { toast.error(isAr ? 'الحجم > 15MB' : 'File > 15MB'); return; }
    setFile(f);
    setExtracted(null);
    setFileId(null);
  };

  const handleExtract = async () => {
    if (!file) return;
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_type', 'bank_letter');
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/company-docs/extract`,
        fd,
        { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } },
      );
      setExtracted(res.data?.data || {});
      setFileId(res.data?.file_id);
      toast.success(isAr ? 'تم استخراج تفاصيل الحساب من رسالة الايبان' : 'Account details extracted');
    } catch (err) {
      toast.error(err.response?.data?.detail || (isAr ? 'فشل التعرف بالذكاء الاصطناعي' : 'AI extraction failed'));
    } finally {
      setExtracting(false);
    }
  };

  const handleSave = async () => {
    if (!extracted) return;
    setSaving(true);
    try {
      await axios.post(BASE, { ...extracted, file_id: fileId }, { headers: getAuthHeaders() });
      toast.success(isAr ? 'تم إضافة الحساب البنكي' : 'Bank account added');
      onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#1a1a2e] border-white/10 text-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="add-bank-account-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Landmark className="h-5 w-5 text-[#d4af37]" />
            {isAr ? 'إضافة حساب بنكي' : 'Add bank account'}
          </DialogTitle>
          <DialogDescription className="text-[#A1A1AA]">
            {isAr
              ? 'ارفع رسالة الايبان (IBAN Letter) من البنك — يقرأ الذكاء الاصطناعي كل التفاصيل تلقائياً.'
              : 'Upload the bank IBAN letter — AI reads all details automatically.'}
          </DialogDescription>
        </DialogHeader>

        {!extracted && (
          <div
            className="border-2 border-dashed border-[#2a2a4e] rounded-2xl p-6 text-center hover:border-[#d4af37]/40 transition-colors"
            onDragOver={(e) => { e.preventDefault(); }}
            onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]); }}
          >
            <input
              type="file"
              accept=".pdf,image/*"
              onChange={(e) => handleFile(e.target.files?.[0])}
              className="hidden"
              id="bank-letter-input"
              data-testid="bank-letter-input"
            />
            <label htmlFor="bank-letter-input" className="cursor-pointer block">
              <Upload className="h-10 w-10 text-[#d4af37] mx-auto mb-3" />
              {file ? (
                <div>
                  <p className="text-white font-medium">{file.name}</p>
                  <p className="text-xs text-[#808080] mt-1">{(file.size / 1024).toFixed(1)} KB · {file.type}</p>
                </div>
              ) : (
                <>
                  <p className="text-white font-medium">{isAr ? 'اسحب رسالة الايبان هنا أو اضغط للاختيار' : 'Drag the IBAN letter here or click to choose'}</p>
                  <p className="text-xs text-[#808080] mt-1">PDF / PNG / JPG · max 15MB</p>
                </>
              )}
            </label>
          </div>
        )}

        {file && !extracted && (
          <Button
            onClick={handleExtract}
            disabled={extracting}
            className="w-full bg-[#d4af37] hover:bg-[#c9a227] text-black mt-2"
            data-testid="bank-extract-btn"
          >
            {extracting ? (
              <><Loader2 className="h-4 w-4 animate-spin me-2" />{isAr ? 'الذكاء الاصطناعي يقرأ الرسالة...' : 'AI is reading the letter...'}</>
            ) : (
              <><Sparkles className="h-4 w-4 me-2" />{isAr ? 'قراءة تفاصيل الحساب تلقائياً' : 'Auto-read account details'}</>
            )}
          </Button>
        )}

        {extracted && (
          <div className="space-y-3 mt-2" data-testid="bank-extracted-fields">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm">
              <Sparkles className="h-4 w-4" />
              {isAr ? 'تم استخراج التفاصيل. راجعها قبل الحفظ.' : 'Details extracted. Review before saving.'}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {FIELDS.map((f) => (
                <div key={f.key}>
                  <Label className="text-[#A1A1AA] text-xs">{isAr ? f.ar : f.en}</Label>
                  <Input
                    value={extracted[f.key] ?? ''}
                    onChange={(e) => setExtracted((prev) => ({ ...prev, [f.key]: e.target.value }))}
                    className={`bg-[#0a0a0a] border-[#2a2a4e] text-white mt-1 ${f.mono ? 'font-mono' : ''}`}
                    dir={f.mono ? 'ltr' : undefined}
                    data-testid={`bank-new-${f.key}`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-white">
            <X className="h-4 w-4 me-1" />
            {isAr ? 'إلغاء' : 'Cancel'}
          </Button>
          {extracted && (
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              data-testid="bank-save-new-btn"
            >
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Save className="h-4 w-4 me-1" />{isAr ? 'إضافة الحساب' : 'Add account'}</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
